import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, Monitor, CheckCircle2, Clock, Shield, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';

const LaptopScreenRepairPage = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Заміна екрану ноутбука в Одесі - CIMM | Швидко і якісно</title>
        <meta name="description" content="Професійна заміна екрану ноутбука в Одесі. Оригінальні матриці, гарантія, ремонт за 1-3 дні. Ціни від 1500 грн. ☎ +38 097 417 8788" />
      </Helmet>

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Телефони</Link>
            <Link to="/laptops" className="text-orange-500">Ноутбуки</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Контакти</Link>
          </nav>
          <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300">
            +38 097 417 8788
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4 dark-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          <Link to="/laptops" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Повернутися до всіх послуг
          </Link>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center">
                <Monitor className="w-10 h-10 text-orange-500 icon-glow" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Заміна <span className="neon-text">екрану</span> ноутбука
            </h1>
            <p className="text-xl text-gray-400">Швидко, якісно, з гарантією</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 dark-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          
          {/* Key Benefits */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="glass-card p-6 text-center">
              <Clock className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">1-3 дні</h3>
              <p className="text-gray-400 text-sm">Швидкий ремонт</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Shield className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Гарантія</h3>
              <p className="text-gray-400 text-sm">На матрицю і роботу</p>
            </div>
            <div className="glass-card p-6 text-center">
              <CheckCircle2 className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Оригінал</h3>
              <p className="text-gray-400 text-sm">Якісні матриці</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Wrench className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Досвід</h3>
              <p className="text-gray-400 text-sm">10+ років ремонту</p>
            </div>
          </div>

          {/* Description */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Коли потрібна заміна екрану?</h2>
              <ul className="space-y-4">
                {[
                  'Розбитий або тріснутий екран',
                  'Темні плями або білі лінії на дисплеї',
                  'Мерехтіння або рябіння зображення',
                  'Відсутність зображення при включенні',
                  'Вигоріння пікселів або плям',
                  'Механічне пошкодження матриці'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* Image */}
              <div className="mt-6 rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1721333088976-33173bdc59a2" 
                  alt="Ремонт ноутбука"
                  className="w-full h-48 object-cover"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">Майстер за роботою</p>
              </div>
            </div>

            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Що ми робимо?</h2>
              <div className="space-y-4">
                {[
                  { step: '1', text: 'Безкоштовна діагностика', desc: 'Визначаємо тип та розмір матриці' },
                  { step: '2', text: 'Підбір матриці', desc: 'Оригінал або якісний аналог' },
                  { step: '3', text: 'Акуратна заміна', desc: 'Розбираємо, встановлюємо, збираємо' },
                  { step: '4', text: 'Тестування', desc: 'Перевіряємо якість зображення' },
                  { step: '5', text: 'Калібрування', desc: 'Налаштовуємо кольори та яскравість' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-500 font-bold">{item.step}</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold">{item.text}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Video Placeholder */}
              <div className="mt-6 bg-gray-900/50 rounded-lg p-6 border border-orange-500/20 text-center">
                <p className="text-gray-400 text-sm mb-2">📹 Процес заміни матриці</p>
                <p className="text-xs text-gray-500">Наші роботи в <a href="https://www.instagram.com/cimmodua/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">Instagram</a></p>
              </div>
            </div>
          </div>

          {/* Detailed Process */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Детальний процес заміни екрану ноутбука</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Визначення типу матриці</h3>
                  <p className="text-gray-400">Спочатку ми визначаємо точну модель вашого ноутбука та тип матриці. Існують різні типи: TN, IPS, OLED. Також важливі параметри: роздільна здатність, частота оновлення, наявність сенсорного шару.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Розбирання кришки дисплею</h3>
                  <p className="text-gray-400">Акуратно знімаємо пластикову рамку навколо екрану. У більшості ноутбуків вона тримається на защіпках. Потім викручуємо гвинти, які кріплять матрицю до металевої кришки.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">Встановлення нової матриці</h3>
                  <p className="text-gray-400">Від'єднуємо шлейф від старої матриці, встановлюємо нову, під'єднуємо шлейф. Збираємо кришку у зворотному порядку. Тестуємо зображення на різних кольорах та яскравості. Калібруємо кольори для найкращого відображення.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Орієнтовна вартість</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">Стандартні моделі</h3>
                <p className="text-gray-400 text-sm mb-4">Dell, HP, Lenovo, Asus (15.6")</p>
                <p className="text-3xl font-bold text-orange-500">від 1500 грн</p>
              </div>
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">Premium моделі</h3>
                <p className="text-gray-400 text-sm mb-4">Dell XPS, HP Spectre (13-14")</p>
                <p className="text-3xl font-bold text-orange-500">від 2500 грн</p>
              </div>
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">MacBook</h3>
                <p className="text-gray-400 text-sm mb-4">Air, Pro Retina</p>
                <p className="text-3xl font-bold text-orange-500">від 4000 грн</p>
              </div>
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">
              * Точна ціна після діагностики. Діагностика безкоштовна.
            </p>
          </div>

          {/* FAQ */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Часті питання</h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Скільки часу займає заміна екрану?',
                  a: 'Залежить від моделі та наявності матриці. Зазвичай 1-3 дні. Якщо матриця є в наявності - можемо зробити за день.'
                },
                {
                  q: 'Яка гарантія на матрицю?',
                  a: 'Надаємо гарантію від 3 до 6 місяців на матрицю та виконані роботи.'
                },
                {
                  q: 'Чи можна замінити тільки скло?',
                  a: 'У більшості сучасних ноутбуків скло та матриця склеєні разом. Тому зазвичай міняється весь модуль.'
                },
                {
                  q: 'Чи втрачу я дані при заміні екрану?',
                  a: 'Ні, заміна екрану не впливає на дані. Всі файли збережуться на жорсткому диску.'
                }
              ].map((item, idx) => (
                <details key={idx} className="bg-gray-900/30 rounded-lg border border-orange-500/20 overflow-hidden">
                  <summary className="p-4 cursor-pointer text-white font-semibold hover:bg-orange-500/10 transition-colors">
                    {item.q}
                  </summary>
                  <div className="px-4 pb-4 text-gray-300 border-t border-orange-500/20 pt-4">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="cta-card p-8 text-center cta-border">
            <h2 className="text-3xl font-bold text-white mb-4">Потрібна заміна екрану?</h2>
            <p className="text-gray-400 mb-6">Зателефонуйте нам для безкоштовної діагностики</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+380974178788">
                <Button className="futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0" size="lg">
                  Зателефонувати зараз
                </Button>
              </a>
              <Link to="/contacts">
                <Button variant="outline" className="neon-border border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-transparent" size="lg">
                  Де ми знаходимось
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="cyber-footer py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center text-gray-400">
            <p>© 2026 CIMM Computers. Всі права захищені.</p>
            <p className="text-xs text-gray-500 mt-3 max-w-3xl mx-auto">Незалежний сервісний центр з постгарантійного обслуговування. Ми не є офіційним (авторизованим) сервісним центром і не пов'язані з виробниками техніки (Apple, Samsung, Sony, Xiaomi та ін.). Усі назви брендів належать їхнім власникам.</p>
            <p className="mt-2">
              <Link to="/privacy-policy" className="hover:text-orange-500 transition-colors">Політика конфіденційності</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LaptopScreenRepairPage;
